/**
 * WAF service-detail hook — resolves the provisioned Owl service so the console
 * can guard access (403/404 → redirect) before firing any data query. Mirrors
 * the media console's `useServiceDetail`: the v3 backend can answer HTTP 200
 * with a soft status in the body (`{ status: 403, data: { provider_scope } }`).
 */
import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { serviceApiV3, wafKeys } from './_shared';

export interface WafServiceZone {
  id: string;
  zone_id: string;
  name?: string;
  status?: string;
}

export interface WafServiceDetail {
  id: string;
  service_name?: string;
  provider_scope?: string;
  status?: string;
  zones: WafServiceZone[];
  created_at?: string;
  updated_at?: string;
}

interface Envelope<T> {
  status?: number;
  data: T;
  message?: string;
}

type ServiceDetailEnvelope =
  | Envelope<{ service: WafServiceDetail }>
  | Envelope<WafServiceDetail>;

/** Soft (200-body) or hard access failure — consoles redirect on this. */
export class WafServiceAccessError extends Error {
  constructor(
    public readonly status: 403 | 404,
    public readonly providerScope?: string,
  ) {
    super(status === 403 ? 'Access denied' : 'Service not found');
    this.name = 'WafServiceAccessError';
  }
}

function normalize(data: ServiceDetailEnvelope): WafServiceDetail {
  const inner = (data?.data ?? {}) as Record<string, unknown>;
  const svc = ('service' in inner ? inner.service : inner) as WafServiceDetail;
  return { ...svc, zones: svc.zones ?? [] };
}

export function useWafServiceDetail(serviceId: string | undefined) {
  return useQuery<WafServiceDetail>({
    queryKey: wafKeys.service(serviceId ?? ''),
    enabled: !!serviceId,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      if (error instanceof WafServiceAccessError) return false;
      const status = isAxiosError(error) ? error.response?.status : undefined;
      if (status === 403 || status === 404) return false;
      return failureCount < 2;
    },
    queryFn: async () => {
      const res = await serviceApiV3.get<ServiceDetailEnvelope>(`/services/${serviceId}`);
      const soft = Number(res.data?.status);
      if (soft === 403) {
        const inner = res.data?.data as { provider_scope?: string } | undefined;
        throw new WafServiceAccessError(403, inner?.provider_scope);
      }
      if (soft === 404) throw new WafServiceAccessError(404);
      return normalize(res.data);
    },
  });
}
